import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useMe } from '@/api/domains/merchant-settings';
import { requiresMapApiKeys } from '@/api/types/settings';

import { SettingsCard, SettingsSection } from '../components/SettingsSection';
import { AddressReplaceSection } from '../sections/AddressReplaceSection';
import { FreeShippingSection } from '../sections/FreeShippingSection';
import { PointEditCutoffSection } from '../sections/PointEditCutoffSection';
import { PointModeSection } from '../sections/PointModeSection';
import { PointReminderSection } from '../sections/PointReminderSection';
import { PointSearchSection } from '../sections/PointSearchSection';
import { TestModeSection } from '../sections/TestModeSection';

type CheckoutTabProps = {
  dirtyRef: (dirty: boolean) => void;
};

/**
 * Buyer-facing checkout behaviour, grouped into three prototype sections
 * (At checkout / After the order / Testing). Every setting saves immediately
 * (no page-level form), so the tab is never "dirty" - mirrors the
 * pickup-locations/contact-persons tabs.
 */
export function CheckoutTab({ dirtyRef }: CheckoutTabProps) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const meQuery = useMe();

  const settings = meQuery.data?.settings;
  const isShopifyPlus = meQuery.data?.merchant.is_shopify_plus === true;

  // Self-saving sections never carry unsaved form state.
  useEffect(() => {
    dirtyRef(false);
  }, [dirtyRef]);

  if (meQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">{tCommon('states.loading')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <SettingsSection
        title={t('checkout.sections.atCheckout.title')}
        description={t('checkout.sections.atCheckout.description')}
      >
        <SettingsCard>
          <PointModeSection
            mode={settings?.point_selection_mode ?? 'basic'}
            isShopifyPlus={isShopifyPlus}
          />
          {/* The address-search box is part of the map picker - basic mode fans
              points out as native rate options and has no picker to search in. */}
          {requiresMapApiKeys(settings?.point_selection_mode ?? 'basic') ? (
            <PointSearchSection enabled={settings?.point_search_enabled ?? true} />
          ) : null}
          <FreeShippingSection
            calcMode={settings?.free_shipping_calc_mode ?? 'after_discounts'}
            applyMethod={settings?.free_shipping_apply_method ?? 'carrier'}
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title={t('checkout.sections.afterOrder.title')}
        description={t('checkout.sections.afterOrder.description')}
      >
        <SettingsCard>
          <PointReminderSection
            reminderEnabled={settings?.point_reminder_enabled ?? true}
            reminderDelayMinutes={settings?.point_reminder_delay_minutes ?? 5}
            autoSelectEnabled={settings?.point_auto_select_enabled ?? true}
            autoSelectRadiusKm={settings?.point_auto_select_radius_km ?? 5}
          />
          <PointEditCutoffSection
            cutoffEnabled={settings?.point_edit_cutoff_enabled ?? false}
            cutoffMinutes={settings?.point_edit_cutoff_minutes ?? 60}
          />
          <AddressReplaceSection enabled={settings?.point_address_replace_enabled ?? false} />
        </SettingsCard>
      </SettingsSection>

      <TestModeSection enabled={settings?.test_mode_enabled ?? false} />
    </div>
  );
}
